/** Utah's words for library.ts: the Utah-only articles, sections, lines and citations (Utah Code 26B-7, R392, county regulation). */
import type { LibrarySection, LibraryTopic } from "../../library.ts";

const L = (...lines: string[]) => lines.join("\n");

/** Whole article: Utah's food handler permit, certified food safety manager and person-in-charge rules. */
const FOOD_HANDLER_TOPIC: LibraryTopic = {
  id: "food-handler-permits", title: "Food handler permits and food safety managers", category: "people",
  summary: "Utah requires food workers to be trained and permitted, and a certified manager to run each kitchen.",
  sections: [
    { heading: "Food handler permit", body: L(
      "- Finish an approved food handler course within 14 days of starting work.",
      "- Get a food handler permit from the county health department within 30 days of starting.",
      "- The course is at least 75 minutes with a 40-question test; 75% passes.",
      "- The permit is good anywhere in Utah for 3 years.",
      "- The law also says to get the permit before handling food. Some counties may expect it sooner, so check with yours."), citations: ["Utah Code 26B-7-413", "R392-103", "Food Code 2-102.13 (Utah amendment)"] },
    { heading: "Who is exempt", body: L(
      "- A free meal for a disadvantaged group run by a charity is exempt from the food handler permit rule.",
      "- Every food handling rule still applies."), citations: ["Utah Code 26B-7-413(14)"] },
    { heading: "Certified food safety manager", body: L(
      "- Each food business needs at least one supervisor who is a certified food safety manager. They don't have to be there every hour.",
      "- Send proof of a passing exam score to the county health department. The county may not charge to file it.",
      "- If your manager leaves, name a replacement who starts an approved course within 60 days.",
      "- Food trucks need at least one certified manager among the staff. Some counties also require one for home kitchens."), citations: ["Utah Code 26B-7-410", "Utah Code 26B-7-412(1), (6)", "Food Code 2-102.12(A) (Utah amendment)", "R392-102-4(13)"] },
    { heading: "The person in charge", body: L(
      "- The person in charge is the certified manager, or a trained helper who knows the rules and can act.",
      "- They can reject or throw out food, send a sick worker home, and fix problems on the spot.",
      "- Food trucks: a person in charge is on board during all hours.",
      "- Public temporary events: at least one food handler permit holder stays at the booth the whole time."), citations: ["R392-100-4(3)(a)(iv)", "R392-102-15(1)", "County health regulation (Salt Lake County temporary food events)"] },
  ],
  requirementIds: ["ut-food-handler-permit", "ut-cfsm", "ut-mfb-staff"],
  keywords: ["food handler", "food handlers card", "permit", "card", "certificate", "training", "course", "class", "manager", "cfsm", "certified", "person in charge", "pic", "new hire", "14 days", "30 days"],
};

/** Food trucks: cooling at the commissary (R392-102-12(5)). */
const TRUCK_COOL_SECTION: LibrarySection = { heading: "Food trucks", body: "- Cool cooked TCS food at the commissary, not on the truck, unless your health officer exempts you.", citations: ["R392-102-12(5)"] };

/** Microenterprise home kitchens: same-day and 72-hour TCS sales with logs (Utah Code 26B-7-416(8)(a)). */
const MEHK_LOGS_SECTION: LibrarySection = { heading: "Home kitchens are stricter", body: "- A microenterprise home kitchen sells TCS food the same day, or within 72 hours only with temperature logs (items every 2 hours; fridge and freezer at start, end and every 4 hours). Keep the logs 90 days.", citations: ["Utah Code 26B-7-416(8)(a)"] };

/** How to read an inspection report: Utah's priority / priority foundation / core terms (R392-100-4(3)(a)(vi)-(vii)). */
const REPORT_TERMS_SECTION: LibrarySection = { heading: "Reading the report", body: L(
        "- Priority item = \"critical 1.\" Fix it on the spot.",
        "- Priority foundation item = \"critical 2.\"",
        "- Core item = non-critical."), citations: ["R392-100-4(3)(a)(vi)–(vii)", "Source protocols: Utah amendment (inspection report terms)"] };

export const LIBRARY_UT = {
  foodHandlerTopic: FOOD_HANDLER_TOPIC,
  truckCoolSection: TRUCK_COOL_SECTION,
  mehkLogsSection: MEHK_LOGS_SECTION,
  reportTermsSection: REPORT_TERMS_SECTION,
  /** Citation: Utah's illness reporting amendment (R386-702, R386-703) to Food Code 8-501.10(C). */
  illnessCite: "Food Code 8-501.10(C) (Utah amendment: illness reporting under R386-702 and R386-703)",
  /** Sentence: food trucks wash after any drink (R392-102-14(4)(c)). */
  truckDrinkWashSentence: "A food truck washes after any drink (Utah R392-102-14(4)(c)).",
  /** Line: Utah's truck rule has no approval path for bare-hand contact. */
  truckBareHandLine: "- Food trucks: Utah's truck rule has no approval path for bare-hand contact.",
  /** Citation: truck bare-hand contact (R392-102-14(1)-(3)). */
  truckBareHandCite: "R392-102-14(1)–(3)",
  /** Citation: truck employees do not taste raw or undercooked food (R392-102-14(15)). */
  truckTasteCite: "Utah R392-102-14(15) for trucks",
  /** Line: Utah's rule name for TCS food, "potentially hazardous food". */
  phfLine: "Utah's rule calls TCS food \"potentially hazardous food.\" Same thing.",
  /** Citation: Utah's definition of potentially hazardous food (R392-100-4(3)(a)(v)). */
  phfCite: "R392-100-4(3)(a)(v)",
  /** Line: Utah's truck rule does not mention time as a public health control. */
  truckTimeControlLine: "- Food trucks: Utah's truck rule doesn't mention this option. Ask your health department before using it.",
  /** Citation: truck rule silent on time control (R392-102-12(6)). */
  truckTimeControlCite: "docs/source/research/food-truck-utah.md (R392-102-12(6) does not mention time control)",
  /** Citation: truck thermometers, no glass or mercury (R392-102-8(15)-(16)). */
  truckGlassCite: "R392-102-8(15)–(16)",
  /** Line: do not calibrate by boiling water at Utah altitude (boiling point is about 198-206F). */
  boilingLine: "Do not use boiling water in Utah. Water boils at about 198–206°F here depending on elevation, not 212°F, so it can't confirm accuracy.",
  /** Citation: ice-point method only, no boiling-point method in Utah. */
  boilingCite: "Source protocols: ice-point method; do not use the boiling-point method in Utah",
  /** Citation: truck receiving temperatures (R392-102-12(1)). */
  truckReceivingCite: "R392-102-12(1)",
  /** Line: Utah's state meat and poultry inspection mark. */
  stateInspectionMarkLine: "- Meat and poultry carry the USDA inspection mark or Utah's state inspection mark.",
  /** Line: Utah bans wild-picked mushrooms. */
  mushroomBanLine: "- No wild-picked mushrooms (Utah bans them).",
  /** Citation: Utah amendment to Food Code 3-201.16(A), approved sources. */
  approvedSourceAmendCite: "Food Code 3-201.16(A) (Utah amendment)",
  /** Citation: truck approved sources (R392-102-11(2), (7)-(9)). */
  truckSourcesCite: "R392-102-11(2), (7)–(9)",
  /** Citation: truck allergen labeling (R392-102-15(3)). */
  truckAllergenCite: "R392-102-15(3)",
  /** Citation: truck sanitizer checks (R392-102-9(4)-(5)). */
  truckSanitizerCite: "R392-102-9(4)–(5)",
  /** Citation: MEHK dishwashing (Utah Code 26B-7-416(9)(b)). */
  mehkSinkCite: "Utah Code 26B-7-416(9)(b)",
  /** Citation: truck 3-compartment sink (R392-102-6(14)). */
  truckSinkCite: "R392-102-6(14)",
  /** Citation: truck wiping-cloth buckets (R392-102-9(14)). */
  truckBucketCite: "R392-102-9(14)",
  /** Line: MEHK shop linens kept apart from household laundry. */
  mehkLinenLine: "- Home kitchens: shop linens are stored and washed apart from household laundry.",
  /** Citation: MEHK linens (Utah Code 26B-7-416(9)(m)). */
  mehkLinenCite: "Utah Code 26B-7-416(9)(m)",
  /** Citation: truck employee illness procedure in writing (R392-102-15(5)). */
  truckIllnessProcedureCite: "R392-102-15(5)",
  /** Citation: Utah amendment to Food Code 6-202.14, restrooms. */
  restroomAmendCite: "Food Code 6-202.14 (Utah amendment)",
  /** Citation: truck dumpsters (R392-102-6(10)). */
  truckDumpsterCite: "R392-102-6(10)",
  /** Citation: truck pest control (R392-102-13(6), (7), (9)). */
  truckPestCite: "R392-102-13(6), (7), (9)",
  /** Line: Salt Lake County temporary event permit deadline and late fee. */
  slcDeadlineLine: "- Salt Lake County: apply at least 7 days ahead; a late fee applies after that. Nonprofits are not exempt from the fee. Check your own county's deadline.",
  /** Line: Salt Lake County permitted caterer at a nonprofit fundraiser. */
  slcCatererLine: "- In Salt Lake County, a permitted caterer may serve a nonprofit fundraiser without its own temporary permit if it isn't paid and its food safety manager stays the whole event.",
  /** Citation: temporary food events in Utah Code (26B-7-401). */
  tempEventLawCite: "Utah Code 26B-7-401",
  /** Citation: Salt Lake County temporary food events regulation. */
  slcTempEventCite: "County health regulation (Salt Lake County temporary food events)",
  /** Citation: MEHK records (Utah Code 26B-7-416(8)(a)). */
  mehkLogsCite: "Utah Code 26B-7-416(8)(a)",
  /** Citation: truck commissary log (R392-102-3). */
  truckRecordsCite: "R392-102-3",
  /** Line: MEHK standard operating procedures filed with the permit. */
  mehkProceduresLine: "- Home kitchens: the standard operating procedures filed with your permit",
  /** Citation: MEHK procedures (Utah Code 26B-7-416(10)). */
  mehkProceduresCite: "Utah Code 26B-7-416(10)",
  /** Citation: Utah amendment to Food Code 8-905.10(A), impounding food. */
  impoundCiteA: "Food Code 8-905.10(A) (Utah amendment)",
  /** Citation: Utah amendment to Food Code 8-903.10(C), impounding food. */
  impoundCiteB: "Food Code 8-903.10(C) (Utah amendment)",
  /** Line: Salt Lake County inspectors do unscheduled inspections. */
  slcUnscheduledLine: "- Salt Lake County inspectors do unscheduled inspections.",
  /** Line: MEHK inspections only at times the law allows (Utah Code 26B-7-416(6)). */
  mehkInspectionLine: "- Home kitchens (MEHK): the county may inspect only at times the law allows (the first no more than a week before opening) and must document the reason for each later visit.",
  /** Citation: Salt Lake County unscheduled inspections source note. */
  slcUnscheduledCite: "docs/source/research/why-it-matters.md (Salt Lake County: unscheduled inspections)",
  /** Citation: truck inspections (R392-102-16(6), (10)(b)). */
  truckInspectionCite: "R392-102-16(6), (10)(b)",
  /** Citation: MEHK inspections (Utah Code 26B-7-416(6)). */
  mehkInspectionCite: "Utah Code 26B-7-416(6)",
} as const;
